import React, { useState, useEffect, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import MainLayout from "./components/MainLayout/MainLayout";
import ProductsList from "./pages/ProductsList/ProductsList";
import AddProduct from "./pages/AddProduct/AddProduct";
import ProductDetails from "./pages/ProductDetails/ProductDetails";
import EditProduct from "./pages/EditProduct/EditProduct";
import Profile from "./pages/Profile";
import Orders from "./pages/Orders/Orders";
import OrdersDetails from "./pages/Orders/OrdersDetails";
import Reviews from "./pages/Reviews/Reviews";
import Login from "./pages/Login/Login";
import Register from "./pages/Register/Register";
import Suppliers from "./pages/SuppliersList/Suppliers";
import AddSupplier from "./pages/AddSupplier/AddSupplier";
import Dashboard from "./components/Dashboard/Dashboard";
import Supplier from "./pages/Supplier/Supplier";
import Chat from "./pages/Chat/Chat";
import OrganizersTable from "./components/Organizers/OrganizersTable";
import AddOrganizerForm from "./components/Organizers/AddOrganizerForm";
import EditOrganizerForm from "./components/Organizers/EditOrganizerForm";
import OrganizerSuccessPage from "./components/Organizers/OrganizerSuccessPage";
import ProtectedRoute from "../src/contexts/ProtectedRoute";
import { AuthProvider } from "../src/contexts/AuthProvider";
import AuthContext from "./contexts/AuthProvider";
import Loader from "./components/Loader";
import NotFound from "./pages/NotFound";
import './App.css';
import BulkProducts from "./pages/AddBulkProducts/BulkProducts";
import ConsentBanner from "./components/ConsentBanner";
import KlaviyoTracker from "./components/KlaviyoTracker";

function App() {
  // Track current language (default en)
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );

  // Update document direction whenever language changes
  useEffect(() => {
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

  // Listen for language changes in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "language") {
        setLanguage(event.newValue || "en");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ConsentBanner />
        <KlaviyoTracker accountType="Vendor" />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<AuthRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/suppliers/:id" element={<Supplier />} />
            <Route
              path="/suppliers/add-new-supplier"
              element={<AddSupplier />}
            />
            <Route
              path="/suppliers/edite-supplier/:id"
              element={<AddSupplier />}
            />

            <Route path="/products" element={<ProductsList />} />
            <Route path="/products/:id" element={<ProductDetails />} />
            <Route path="/products/add-new-product" element={<AddProduct />} />
            <Route
              path="/products/edit-product/:id"
              element={<EditProduct />}
            />
            <Route path="/products/bulk-upload" element={<BulkProducts />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/:order_id" element={<OrdersDetails />} />
            <Route path="/organizers" element={<OrganizersTable />} />
            <Route path="/organizers/add" element={<AddOrganizerForm />} />
            <Route path="/organizers/success" element={<OrganizerSuccessPage />} />
            <Route path="/organizers/edit/:id" element={<EditOrganizerForm />} />
            <Route path="/messages" element={<Chat />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/settings" element={<Profile />} />
            <Route path="/profile" element={<Profile />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

const AuthRedirect = () => {
  const { isAuthenticated, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  return isAuthenticated ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Navigate to="/login" replace />
  );
};

export default App;
